import {useTheme, useWindowSize} from '@primer/react-brand/esm'
import {useId, useRef, useState} from 'react'

import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {useIntersectionObserver} from '@github-ui/swp-core/lib/hooks/use-intersection-observer'

import {Product} from '../../Product/Product'
import {getHeroMobileCropClassName} from './hero-mobile-crop'
import {useHeroSequencePlayback} from './use-hero-sequence-playback'
import type {HeroSequenceProps} from './types'

import Styles from './HeroSequence.module.css'
import {isBrowser} from '@github-ui/brand-core/utils/platform'

export type {HeroSequenceHandle, HeroSequenceProps} from './types'

export const defaultProps: Partial<HeroSequenceProps> = {}

export const HeroSequence = (props: HeroSequenceProps) => {
  const initializedProps: HeroSequenceProps = {...defaultProps, ...props}
  const {labelledBy, chaptersAltDesc, video, ...playbackProps} = initializedProps
  const {currentChapterIndex} = playbackProps

  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isVideoReady, setIsVideoReady] = useState(false)

  const descriptionId = useId()
  const {colorMode} = useTheme()
  const {isSmall: isTablet, isLarge: isDesktop} = useWindowSize()
  const prefersReducedMotion = usePrefersReducedMotion()
  const {isIntersecting} = useIntersectionObserver(wrapperRef, {threshold: 0.4, isOnce: true})

  const videoUrl = colorMode === 'dark' ? video?.url.dark || video?.url.light : video?.url.light

  const {videoRef, reportPlayback, handleError} = useHeroSequencePlayback({
    ...playbackProps,
    videoUrl,
    chapterTimestamps: video?.chapterTimestamps,
    onActionChange: video?.onActionChange,
    onPlaybackReady: () => setIsVideoReady(true),
    prefersReducedMotion,
  })

  return (
    <div ref={wrapperRef} className={Styles['HeroSequence']}>
      <Product
        theme={colorMode}
        minPadding="var(--base-size-64)"
        backgroundProps={{hasCornerDots: true}}
        mediaPosition={isDesktop ? 'bottom-center' : isTablet ? 'center' : 'center-left'}
        productUI={{
          // TODO: Delete once mobile asset OR moving to HTML-rendered UI
          className: getHeroMobileCropClassName(currentChapterIndex),
          shadowColor: 'green',
          shadowPosition: isDesktop ? 'left' : 'left-bottom',
          roundedCorners: {bottomLeft: !isDesktop, bottomRight: !isDesktop},
        }}
        shouldAnimateIn
        canAnimateIn={isBrowser() && (!videoUrl || isVideoReady) && isIntersecting}
        enterFrom={isTablet ? 'bottom' : 'right'}
      >
        <div className={Styles.HeroSequence__content}>
          <span id={descriptionId} hidden>
            {chaptersAltDesc[currentChapterIndex]}
          </span>
          {videoUrl && (
            <video
              className={Styles.HeroSequence__video}
              ref={videoRef}
              src={videoUrl}
              aria-labelledby={labelledBy}
              aria-describedby={descriptionId}
              muted
              playsInline
              preload="metadata"
              onTimeUpdate={reportPlayback}
              onPlay={reportPlayback}
              onPlaying={reportPlayback}
              onPause={reportPlayback}
              onWaiting={reportPlayback}
              onSeeking={reportPlayback}
              onSeeked={reportPlayback}
              onEnded={reportPlayback}
              onError={handleError}
            />
          )}
          {/* TODO: TBC, add JSON animation player component when not using a video? */}
        </div>
      </Product>
    </div>
  )
}
